import { GraphScale, useGraphCtx } from '@mi-front/core';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import { axisBottom, axisTop, select, timeHour, timeMinute, timeSecond } from 'd3';
import moment from 'moment-timezone';
import { Component } from 'react';

/** Minimum horizontal space for HH:mm:ss tick labels. */
const MIN_PX_PER_SECOND_TICK = 100;
/** Minimum horizontal space for HH:mm tick labels. */
const MIN_PX_PER_MINUTE_TICK = 85;

/** Nice second steps used when the axis shows HH:mm:ss. */
const NICE_SECOND_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
/** Nice minute steps used when the axis shows HH:mm. */
const NICE_MINUTE_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360, 720];

const StyledAxisWrapper = styled('g')(({ theme }) => ({
  '& .tick': {
    fontSize: '11px',
    '& line': {
      stroke: theme.palette.grey[200],
      strokeWidth: '1px',
    },
    '& text': {
      fill: theme.palette.text.primary,
    },
  },
  '& .domain': {
    stroke: theme.palette.grey[200],
    fill: 'none',
    strokeWidth: '1px',
  },
  '& .text': {
    lineHeight: '1rem',
  },
}));

interface Props {
  readonly scale: GraphScale;
  readonly width: number;
  readonly type?: 'bottom' | 'top';
  readonly x?: number;
  readonly y?: number;
  readonly tz?: string;
  readonly axisId?: string;
  readonly hideSeconds?: boolean;
}

/**
 * Picks a d3 time interval so HH:mm:ss labels fit without overlapping.
 * Uses nice second/minute steps derived from range length and available width.
 */
function getSecondsTickInterval(rangeMs: number, width: number) {
  const maxTicks = Math.max(2, Math.floor(width / MIN_PX_PER_SECOND_TICK));
  const rangeSeconds = rangeMs / 1000;
  const rawStepSec = Math.max(1, Math.ceil(rangeSeconds / maxTicks));
  const stepSec = NICE_SECOND_STEPS.find((step) => step >= rawStepSec) ?? rawStepSec;

  if (stepSec < 60) {
    return timeSecond.every(stepSec);
  }

  return timeMinute.every(stepSec / 60);
}

/**
 * Picks a d3 time interval so HH:mm labels stay readable on wide viewports.
 * Caps density via nice minute/hour steps instead of a raw tick count.
 */
function getMinutesTickInterval(rangeMs: number, width: number) {
  const maxTicks = Math.max(2, Math.floor(width / MIN_PX_PER_MINUTE_TICK));
  const rangeMinutes = rangeMs / (60 * 1000);
  const rawStepMin = Math.max(1, Math.ceil(rangeMinutes / maxTicks));
  const stepMin = NICE_MINUTE_STEPS.find((step) => step >= rawStepMin) ?? rawStepMin;

  if (stepMin < 60) {
    return timeMinute.every(stepMin);
  }

  return timeHour.every(stepMin / 60);
}

export class AxisXP extends Component<Props> {
  static defaultProps = {
    type: 'bottom',
    x: 0,
    y: 0,
  };

  private d3Axis: any;
  private root: any;

  componentDidMount() {
    this.applyScale();
  }

  componentDidUpdate() {
    this.applyScale();
  }

  applyScale = () => {
    const { scale, type, width, tz, hideSeconds } = this.props;

    if (type === 'bottom') {
      this.d3Axis = axisBottom(scale);
    } else {
      this.d3Axis = axisTop(scale);
    }

    // Set proper axis time format
    // If hideSeconds is true, always use HH:mm format
    // Otherwise, if the selected zoom range is lower than 13.2 minutes, show seconds as well
    const [from, to] = scale.domain();
    const rangeMs = to.valueOf() - from.valueOf();
    const rangeMinutes = rangeMs / (60 * 1000);
    const showWithSeconds = !hideSeconds && rangeMinutes <= 13.2;

    const timeFormat = showWithSeconds ? 'HH:mm:ss' : 'HH:mm';
    this.d3Axis.tickFormat((d: any) => {
      return (tz ? moment(d).tz(tz) : moment(d)).format(timeFormat);
    });

    // Prefer explicit nice intervals over ticks(count): raw counts scale with
    // width and get too dense on ultra-wide screens (e.g. 1-minute labels).
    if (showWithSeconds) {
      this.d3Axis.ticks(getSecondsTickInterval(rangeMs, width));
    } else {
      this.d3Axis.ticks(getMinutesTickInterval(rangeMs, width));
    }

    select(this.root).call(this.d3Axis);
  };

  render() {
    const { x, y, axisId } = this.props;

    return (
      <StyledAxisWrapper className={classNames('axis-x')}>
        <g
          id={axisId}
          ref={(el) => {
            this.root = el;
          }}
          transform={`translate(${x}, ${y})`}
        />
      </StyledAxisWrapper>
    );
  }
}

type PassedProps = Pick<Props, 'type' | 'x' | 'y' | 'axisId' | 'hideSeconds'>;

export function AxisX(p: PassedProps) {
  const { scale, width, tz } = useGraphCtx();

  return <AxisXP scale={scale} width={width} tz={tz} {...p} />;
}
